import {createFileRoute} from '@tanstack/react-router';
import {useAuth} from '@workos-inc/authkit-react';
import {LoaderCircleIcon} from 'lucide-react';
import * as React from 'react';
import {PageContent} from '@/components/page-content';
import {PageWrapper} from '@/components/page-wrapper';
import {ApiKeysManager} from '@/features/auth/components/api-keys-manager';
import {rootLogger} from '@/features/development/services/root-logger';

const logger = rootLogger.getChildLogger('_app/apikeys');

// Define the route
export const Route = createFileRoute('/_app/apikeys')({
  component: ApiKeysPage,
  async beforeLoad() {
    logger.debug('beforeLoad called for apikeys route');
    return {
      title: 'API Keys',
      breadcrumb: {
        label: 'API Keys',
        path: '/apikeys',
      },
    };
  },
});

function ApiKeysPage() {
  const {user} = useAuth();
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  logger.debug('ApiKeysPage component rendering', {userExists: Boolean(user)});

  React.useEffect(() => {
    const checkAdminStatus = () => {
      try {
        setIsLoading(true);
        setError(null);
      } catch (error_) {
        setError(error_ instanceof Error ? error_ : new Error('Unknown error'));
        logger.error('Error checking admin status:', error_);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      logger.debug('User exists, checking admin status');
      checkAdminStatus();
    } else {
      logger.debug('No user found, skipping admin check');
    }
  }, [user]);

  if (isLoading) {
    return (
      <PageWrapper>
        <PageContent className="p-6">
          <div className="flex h-64 items-center justify-center">
            <LoaderCircleIcon className="mr-2 size-8 animate-spin" />
            <span>Loading...</span>
          </div>
        </PageContent>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <PageContent className="p-6">
          <div className="text-red-500">
            <h1 className="mb-2 text-2xl font-bold">Error</h1>
            <p>{error.message}</p>
          </div>
        </PageContent>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageContent className="p-6">
        <h1 className="mb-2 text-2xl font-bold">API Keys</h1>
        <p className="mb-6 text-gray-600">
          Manage API keys for your organization. API keys can be used to authenticate API requests.
        </p>
        <ApiKeysManager />
      </PageContent>
    </PageWrapper>
  );
}
